// ═══════════════════════════════════════════════════════════════════════════
// ADMIN DASHBOARD v6 - MENU CULTURES NETTOYÉ
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/AdminDashboard.jsx
// Suppression des démos, nouvelle structure menu Cultures
// Farm-Booking - Papy 73+ - Janvier 2026
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Menu, X, Users } from 'lucide-react';

// Imports des composants
import GestionProduits from './components/GestionProduits';
import DashboardStats from './components/DashboardStats';
import GestionClients from './components/GestionClients';
import EtatClients from './components/EtatClients';
import ParametresFerme from './components/ParametresFerme';
import GestionNotifications from './components/GestionNotifications';
import PlanningProduction from './components/PlanningProduction';
import DashboardCockpit from './components/DashboardCockpit';
import GestionSite from './components/GestionSite'; 
import GestionHomePage from './components/GestionHomePage';
import GestionCategories from './components/GestionCategories';
import MemoEntitesHTML from './components/MemoEntitesHTML';
import ParametresPlanning from './components/ParametresPlanning';
// GestionStatutsCommandes supprimé - Utiliser GestionStatutsCommandesV2 dans le Cockpit
// GestionImpression supprimé - Routes backend conservées pour impression thermique
import GestionPeriodes from './components/GestionPeriodes';
import GestionSecurite from './components/GestionSecurite';
import WidgetMeteo from './components/WidgetMeteo';
import GestionRecolte from './components/GestionRecolte';
import GestionComptoir from './components/GestionComptoir';
import GestionHRUsers from './components/GestionHRUsers';

// ═══════════════════════════════════════════════════════════════════════════
// IMPORTS CULTURES - NOUVEAU MENU NETTOYÉ
// ═══════════════════════════════════════════════════════════════════════════
import PlanMaraichage from './components/PlanMaraichage';           // 🗺️ Plan terrain
import TableauCulturesAnnuel from './components/TableauCulturesAnnuel'; // 📊 Timeline annuelle
import GestionCultures from './components/GestionCultures';         // 🌱 Liste cultures
import SimulateurIoT from './components/SimulateurIoT';             // 📡 Supervision IoT
import GestionSemences from './components/GestionSemences';         // 🌱 Caverne semences
import FichesCulturales from './components/FichesCulturales';       // 📋 Référentiel FAO
import GestionIoTModules from './components/GestionIoTModules';     // ⚙️ Config modules
import GestionParcelles from './components/GestionParcelles';       // 📍 CRUD Parcelles
import { GrafcetEditor } from '../grafcet';                         // 🔧 Éditeur GRAFCET


const AdminDashboard = ({ config: configFromApp }) => {
  const [activeTab, setActiveTab] = useState('cockpit');
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [config, setConfig] = useState(configFromApp || {});
  const [dashboardData, setDashboardData] = useState({
    produits: 0,
    paniersPrets: 0,
    aCueillir: 0,
    caSemaine: 0,
    reservations: []
  });
  const [loading, setLoading] = useState(true);

  // Synchroniser config
  useEffect(() => {
    if (configFromApp) {
      setConfig(configFromApp);
    }
  }, [configFromApp]);

  // Fonction pour recharger la config
  const loadConfig = async () => {
    try {
      const response = await fetch('/api/configuration');
      const data = await response.json();
      
      const configObj = {};
      if (Array.isArray(data)) {
        data.forEach(item => {
          configObj[item.param_name] = item.param_value;
        });
      } else if (data.data && Array.isArray(data.data)) {
        data.data.forEach(item => {
          configObj[item.param_name] = item.param_value;
        });
      }
      
      setConfig(configObj);
    } catch (error) {
      console.error('Erreur chargement config:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGER LES DONNÉES DU DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  const loadDashboardData = async () => {
    try {
      const produitsRes = await fetch('/api/produits');
      const produitsData = await produitsRes.json();
      
      const clientsRes = await fetch('/api/clients');
      const clientsData = await clientsRes.json();
      
      const reservationsRes = await fetch('/api/reservations').catch(() => ({ ok: false }));
      let reservationsData = [];
      if (reservationsRes.ok) {
        const data = await reservationsRes.json();
        reservationsData = data.data || data || [];
      }

      const produits = produitsData.success ? produitsData.data : [];
      const clients = clientsData.success ? clientsData.data : [];
      
      // CA SEMAINE
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const caSemaine = reservationsData
        .filter(r => {
          const dateResa = new Date(r.date_reservation || r.created_at);
          return dateResa >= weekAgo && dateResa <= today;
        })
        .filter(r => !['annulee'].includes(r.statut))
        .reduce((sum, r) => sum + (parseFloat(r.total) || parseFloat(r.montant_estime) || 0), 0);

      // COMPTEURS STATUTS
      const paniersPrets = reservationsData.filter(r => r.statut === 'prete').length;
      const aCueillir = reservationsData.filter(r => 
        ['en_preparation', 'a_preparer', 'confirmee'].includes(r.statut)
      ).length;

      setDashboardData({
        produits: produits.length,
        produitsActifs: produits.filter(p => p.actif === 'oui').length,
        stockFaible: produits.filter(p => p.quantite_disponible <= 5).length,
        clients: clients.length,
        clientsActifs: clients.filter(c => c.actif === 'actif').length,
        paniersPrets: paniersPrets,
        aCueillir: aCueillir,
        caSemaine: caSemaine.toFixed(2),
        reservations: reservationsData.slice(0, 10)
      });

      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadConfig();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Pages de Configuration
  const configurationPages = ['categories', 'general', 'homepage', 'site', 'notifications', 'templates', 'planning_resa', 'periodes', 'securite'];
  const isInConfiguration = configurationPages.includes(activeTab);

  // Auto-afficher le sous-menu si on est dans Configuration
  useEffect(() => {
    if (isInConfiguration) {
      setShowConfiguration(true);
    }
  }, [isInConfiguration]);

  // ═══════════════════════════════════════════════════════════════════════════
  // MENU PRINCIPAL - NETTOYÉ (sans Réservations ni Impression)
  // ═══════════════════════════════════════════════════════════════════════════
  const mainTabs = [
    { id: 'cockpit', label: '🚜 Cockpit' },
    { id: 'gestion_rapide', label: '📊 Vue' },
    { id: 'produits', label: '📦 Produits' },
    { id: 'clients', label: '👥 Clients', hasSubMenu: true },
    { id: 'cultures', label: '🌱 Cultures', hasSubMenu: true },
    { id: 'recolte', label: '🌾 Récolte' },
    { id: 'comptoir', label: '🏪 Comptoir' },
    { id: 'meteo', label: '🌤️ Météo' }
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // SOUS-MENU CULTURES - NETTOYÉ ET RÉORGANISÉ
  // ═══════════════════════════════════════════════════════════════════════════
  const culturesSubTabs = [
    { id: 'tableau_annuel', label: '📊 Tableau Annuel' },      // Vue Gantt timeline
    { id: 'plan_maraichage', label: '🗺️ Plan Maraîchage' },   // Carte du terrain
    { id: 'gestion_cultures', label: '🌱 Cultures' },          // Liste cultures actives
    { id: 'gestion_parcelles', label: '📍 Parcelles' },        // CRUD Parcelles/Zones
    { id: 'simulateur_iot', label: '📡 Supervision IoT' },     // Temps réel capteurs
    { id: 'gestion_semences', label: '🌱 Caverne Semences' },  // Stock graines
    { id: 'fiches_culturales', label: '📋 Fiches FAO' },       // Référentiel
    { id: 'iot_modules', label: '⚙️ Config IoT' },             // Admin ESP32
    { id: 'grafcet_editor', label: '🔧 Éditeur GRAFCET' },     // Automation
  ];

  // Sous-menu Clients
  const clientsSubTabs = [
    { id: 'liste', label: '📋 Liste Clients' },
    { id: 'stats', label: '📊 Statistiques' }
  ];

  // Sous-menu Configuration
  const configurationTabs = [
    { id: 'categories', label: '📁 Catégories' },
    { id: 'general', label: '🏠 Général' },
    { id: 'homepage', label: '🎨 Page Accueil' },
    { id: 'site', label: '🌐 Site Web' },
    { id: 'notifications', label: '📧 Notifications' },
    { id: 'templates', label: '📝 Templates' },
    { id: 'planning_resa', label: '📅 Planning Résa' },
    { id: 'periodes', label: '📆 Périodes' },
    { id: 'securite', label: '🔐 Sécurité' },
    { id: 'hr-users', label: '👤 Comptes Admin', icon: Users }
  ];

  // Gestion du changement d'onglet
  const handleTabChange = (tabId, hasSubMenu = false) => {
    setActiveTab(tabId);
    if (hasSubMenu) {
      // Définir le sous-onglet par défaut
      if (tabId === 'clients') {
        setActiveSubTab('liste');
      } else if (tabId === 'cultures') {
        setActiveSubTab('tableau_annuel');
      }
    } else {
      setActiveSubTab(null);
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ═══════════════════════════════════════════════════════════════════════════
          NAVBAR PRINCIPAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      <nav className="bg-green-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <span className="text-2xl mr-2">🌱</span>
              <span className="font-bold text-xl">Farm-Booking Admin</span>
            </div>

            {/* Menu Desktop */}
            <div className="hidden lg:flex items-center space-x-1">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id, tab.hasSubMenu)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-green-800 text-white'
                      : 'text-green-100 hover:bg-green-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              
              {/* Bouton Configuration */}
              <button
                onClick={() => setShowConfiguration(!showConfiguration)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isInConfiguration
                    ? 'bg-green-800 text-white'
                    : 'text-green-100 hover:bg-green-600'
                }`}
              >
                ⚙️ Configuration {isInConfiguration || showConfiguration ? '▼' : '▶'}
              </button>
            </div>

            {/* Menu Mobile Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-green-600"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════════════════
          MENU MOBILE
          ═══════════════════════════════════════════════════════════════════════════ */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-green-600 text-white">
          <div className="px-4 py-3 space-y-1">
            <nav className="space-y-1">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id, tab.hasSubMenu)}
                  className={`block w-full text-left py-2 px-4 rounded-md ${
                    activeTab === tab.id
                      ? 'bg-green-700 font-medium'
                      : 'hover:bg-green-500'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              
              {/* Configuration dans mobile */}
              <button
                onClick={() => setShowConfiguration(!showConfiguration)}
                className={`block w-full text-left py-2 px-4 rounded-md ${
                  isInConfiguration ? 'bg-green-700 font-medium' : 'hover:bg-green-500'
                }`}
              >
                ⚙️ Configuration {isInConfiguration || showConfiguration ? '▼' : '▶'}
              </button>

              {/* Sous-menu Configuration mobile */}
              {(showConfiguration || isInConfiguration) && (
                <div className="pl-6 space-y-1 border-l-2 border-green-300">
                  {configurationTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`text-left w-full py-2 px-4 rounded-md text-sm ${
                        activeTab === tab.id
                          ? 'bg-green-600 text-white font-medium'
                          : 'text-green-800 hover:bg-green-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          SOUS-MENU CONFIGURATION - DESKTOP
          ═══════════════════════════════════════════════════════════════════════════ */}
      {(showConfiguration || isInConfiguration) && (
        <div className="hidden lg:block bg-green-50 border-b border-green-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-4 overflow-x-auto py-2">
              {configurationTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 rounded-md text-base font-medium whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-green-600 text-white'
                      : 'text-green-800 hover:bg-green-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          SOUS-MENU CLIENTS
          ═══════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'clients' && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-4 py-2">
              {clientsSubTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`px-4 py-2 rounded-md text-base font-medium ${
                    activeSubTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-blue-800 hover:bg-blue-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          SOUS-MENU CULTURES - NOUVEAU MENU NETTOYÉ
          ═══════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'cultures' && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-4 py-2 overflow-x-auto">
              {culturesSubTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`px-4 py-2 rounded-md text-base font-medium whitespace-nowrap ${
                    activeSubTab === tab.id
                      ? 'bg-emerald-600 text-white'
                      : 'text-emerald-800 hover:bg-emerald-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          CONTENU DES ONGLETS
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && activeTab === 'gestion_rapide' ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-lg text-gray-500">Chargement des statistiques...</div>
          </div>
        ) : (
          <>
            {/* ═══════════════════════════════════════════════════════════════════
                ONGLETS PRINCIPAUX
                ═══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'cockpit' && <DashboardCockpit />}
            {activeTab === 'gestion_rapide' && <DashboardStats dashboardData={dashboardData} onNavigate={(tab) => setActiveTab(tab)} />}
            {activeTab === 'produits' && <GestionProduits />}
            
            {/* ═══════════════════════════════════════════════════════════════════
                CLIENTS avec sous-onglets
                ═══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'clients' && activeSubTab === 'liste' && <GestionClients />}
            {activeTab === 'clients' && activeSubTab === 'stats' && <EtatClients />}
            
            {/* ═══════════════════════════════════════════════════════════════════
                CULTURES avec sous-onglets - NOUVEAU MENU NETTOYÉ
                ═══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'cultures' && activeSubTab === 'tableau_annuel' && <TableauCulturesAnnuel />}
            {activeTab === 'cultures' && activeSubTab === 'plan_maraichage' && <PlanMaraichage />}
            {activeTab === 'cultures' && activeSubTab === 'gestion_cultures' && <GestionCultures />}
            {activeTab === 'cultures' && activeSubTab === 'gestion_parcelles' && <GestionParcelles />}
            {activeTab === 'cultures' && activeSubTab === 'simulateur_iot' && <SimulateurIoT />}
            {activeTab === 'cultures' && activeSubTab === 'gestion_semences' && <GestionSemences />}
            {activeTab === 'cultures' && activeSubTab === 'fiches_culturales' && <FichesCulturales />}
            {activeTab === 'cultures' && activeSubTab === 'iot_modules' && <GestionIoTModules />}
            {activeTab === 'cultures' && activeSubTab === 'grafcet_editor' && <GrafcetEditor />}

            {/* ═══════════════════════════════════════════════════════════════════
                AUTRES ONGLETS PRINCIPAUX
                ═══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'recolte' && <GestionRecolte />}
            {activeTab === 'comptoir' && <GestionComptoir />}
            {activeTab === 'meteo' && <WidgetMeteo />}

            {/* ═══════════════════════════════════════════════════════════════════
                SOUS-MENU CONFIGURATION
                ═══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'categories' && <GestionCategories />}
            {activeTab === 'general' && <ParametresFerme config={config} onConfigUpdate={loadConfig} />}
            {activeTab === 'homepage' && <GestionHomePage />}
            {activeTab === 'site' && <GestionSite />}
            {activeTab === 'notifications' && <GestionNotifications />}
            {activeTab === 'templates' && <MemoEntitesHTML />}
            {activeTab === 'planning_resa' && <ParametresPlanning />}
            {activeTab === 'periodes' && <GestionPeriodes />}
            {activeTab === 'securite' && <GestionSecurite />}
            {activeTab === 'hr-users' && <GestionHRUsers />}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;